import React, { FC, useEffect, useState } from 'react';
import { Col, Row, Form } from 'antd';
import { useTranslate } from '@refinedev/core';
import { Input, Select, Textarea, FormItem } from 'components/ui';
import { useDictionarySelectProps } from 'hooks/preliminary-application/useDictionarySelectProps';
import { useProcedureSelectProps } from 'hooks/useProcedureSelectProps';
import ProcedureDescription from 'components/preliminary-application/procedure-description';
import styles from './steps.module.scss';

type ProcedureFilterMode = 'excludePrice' | 'onlyPrice' | 'none';

interface Step2CertificateProcedureProps {
    procedureFilterMode?: ProcedureFilterMode;
    autoSelectFirstProcedure?: boolean;
    showProcedureFields?: boolean;
}

export const Step2CertificateProcedure: FC<Step2CertificateProcedureProps> = ({
    procedureFilterMode = 'excludePrice',
    autoSelectFirstProcedure = false,
    showProcedureFields = true,
}) => {
    const t = useTranslate();
    const form = Form.useFormInstance();

    const { applicantProps } = useDictionarySelectProps();

    const { CustomProcedureSelectProps, procedureQueryResult, procedureProps } =
        useProcedureSelectProps({
            filterMode: procedureFilterMode,
            optionLabel: 'name_for_applicant',
        });

    const [procedureOptions, setProcedureOptions] = useState<any[] | null>(
        null
    );

    useEffect(() => {
        const fetchData = async () => {
            const items = await CustomProcedureSelectProps();
            setProcedureOptions(items);

            if (autoSelectFirstProcedure && items && items.length > 0) {
                const currentProcedureId = form?.getFieldValue('procedure_id');
                if (!currentProcedureId) {
                    form?.setFieldsValue({
                        procedure_id: items[0].value as number,
                    });
                }
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [procedureQueryResult.data, autoSelectFirstProcedure, form]);

    return (
        <div className={styles.stepContainer}>
            <h2 className={styles.stepTitle}>
                {t(
                    'preliminary-applications.wizard.step2.title',
                    'Відомості про посвідчення та процедуру'
                )}
            </h2>

            <Row gutter={[16, 20]}>
                <Col xs={24}>
                    <FormItem name="certificate">
                        <Input
                            label={t(
                                'preliminary-applications.fields.certificate',
                                'Реєстраційне посвідчення'
                            )}
                        />
                    </FormItem>
                </Col>

                <Col xs={24}>
                    <FormItem name="applicant_id">
                        <Select
                            {...applicantProps}
                            allowClear
                            label={t(
                                'preliminary-applications.fields.applicant',
                                'Заявник (название реєстраційного посвідчення)'
                            )}
                        />
                    </FormItem>
                </Col>

                <Col xs={24}>
                    <FormItem
                        name="notification_email"
                        rules={[
                            {
                                type: 'email',
                                message: t(
                                    'validation.invalid_email',
                                    'Invalid email format'
                                ),
                            },
                        ]}
                    >
                        <Input
                            type="email"
                            label={t(
                                'preliminary-applications.fields.notification_email',
                                'Email для повідомлень'
                            )}
                        />
                    </FormItem>
                </Col>

                {showProcedureFields && (
                    <>
                        <Col xs={24}>
                            <FormItem name="procedure_id">
                                <Select
                                    {...procedureProps}
                                    options={procedureOptions ?? []}
                                    allowClear
                                    label={t(
                                        'preliminary-applications.fields.procedure',
                                        'Процедура'
                                    )}
                                    requiredMark
                                />
                            </FormItem>
                        </Col>

                        <Col xs={24}>
                            <FormItem name="procedure_desc">
                                <Textarea
                                    label={t(
                                        'preliminary-applications.fields.procedure_desc',
                                        'Опис процедури'
                                    )}
                                />
                            </FormItem>

                            <ProcedureDescription
                                description={
                                    '* ' +
                                    t(
                                        'preliminary-applications.titles.procedure_registration_description_of_the_procedure_type_of_medicinal_product_reference_medicinal_product_nprocedure_renewal_description_of_the_procedure_is_not_applicable_not_active_nprocedure_amendments_description_of_the_procedure_content_of_proposed_amendments',
                                        {
                                            interpolation: {
                                                escapeValue: false,
                                            },
                                        }
                                    )
                                }
                            />
                        </Col>
                    </>
                )}
            </Row>
        </div>
    );
};
